import { Col, Row, Spin } from 'antd';
import classNames from 'classnames';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'redux/hooks';

import { MATCHING } from '@common/constants';
import {
    matchingCheckSkipToken,
    useEquivalentProductMatching,
} from '@common/hooks/useEquivalentProductMatching/useEquivalentProductMatching';
import { useEquivalentProductMatchingSummaryAlerts } from '@common/hooks/useEquivalentProductMatching/useEquivalentProductMatchingSummaryAlerts';
import { useLazyEquivalentProductMatchingSummaryAlerts } from '@common/hooks/useEquivalentProductMatching/useLazyEquivalentProductMatchingSummaryAlerts';
import { ColorModel, MaterialModel } from '@common/models';
import MaterialCard from '@components/cards/material-card/material-card';
import { CardItem } from '@core/components';
import ConfirmationDialog from '@core/components/confirmation-dialog/ConfirmationDialog';
import {
    ConfirmationDialogWithBanner,
    DialogBanner,
} from '@core/components/confirmation-dialog/ConfirmationDialogWithBanner';
import { useAnalyticsEvents } from '@core/hooks/useAnalyticsEvents';
import { useRetailerSettings } from '@core/hooks/useRetailerSettings';
import useUpdateWithHistory from '@core/hooks/useUpdateWithHistory';
import useMaterials from '@ee-api/materials-api/useMaterials';
import { useSelectedMaterial } from '@ee-api/materials-api/useSelectedMaterial';
import { useStores } from '@ee-api/stores-api/useStores';
import { DrawingShapeContext, useOptionalProductContext, usePricedProductsContext } from '@ee-context';
import { resetSelectedChipMinimizers } from '@redux/slices/chipMinimizerSlice';
import { resetSelectedColor } from '@redux/slices/colorSlice';
import { resetCookingAppliances } from '@redux/slices/cookingApplianceSlice';
import { resetSelectedCornerRadius } from '@redux/slices/cornerRadiiSlice';
import { resetSelectedCustomSide } from '@redux/slices/customSideSlice';
import { resetSelectedCutouts } from '@redux/slices/cutoutSlice';
import { resetSelectedDiagonalCorner } from '@redux/slices/diagonalCornerSlice';
import { resetDishwashers } from '@redux/slices/dishwasherSlice';
import { resetSelectedEdge } from '@redux/slices/edgeSlice';
import { resetFaucets } from '@redux/slices/faucetSlice';
import {
    MaterialAndColor,
    resetRequestedColor,
    selectRequestedColor,
    updateRequestedColor,
} from '@redux/slices/materialSlice';
import { resetCountertopAreaQuantity } from '@redux/slices/pricedProductsSlice';
import { resetRefrigerators } from '@redux/slices/refrigeratorSlice';
import { resetSinks } from '@redux/slices/sinkSlice';
import { resetSelectedTearOuts } from '@redux/slices/tearOutSlice';
import { selectZipcode } from '@redux/slices/zipcodeSlice';

import VisualizeIcon from '../../../../../../public/assets/icons/visualize.svg';

import './materials.scss';

export interface MaterialsProps {
    step: number;
    stepInfo: string;
    disabled?: boolean;
}

const Materials = ({ stepInfo, step, disabled = false }: MaterialsProps): JSX.Element => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { stores, isFetchingStores } = useStores();
    const zipcode = useAppSelector(selectZipcode);

    const { materials, isFetchingMaterials } = useMaterials();
    const { selectedValue: selectedMaterial } = useSelectedMaterial();

    const { resetDrawingShapes, clearDrawingShapesUndoStack, resetActiveShape } = useContext(DrawingShapeContext);

    const { resetPricedProducts } = usePricedProductsContext();
    const { resetSelectedOptionalProducts } = useOptionalProductContext();

    const { areThereShapesInTheDrawingArea } = useContext(DrawingShapeContext);

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [temporaryMaterial, setTemporaryMaterial] = useState<MaterialModel | undefined>();
    const { setSelectedMaterialWithHistory } = useUpdateWithHistory();
    const { retailerSettings } = useRetailerSettings();

    const { replaceProductsWithEquivalents } = useEquivalentProductMatching();
    const { LoaderOnAlerts, replaceIfNoAlerts } = useLazyEquivalentProductMatchingSummaryAlerts();
    const isEquivalentProductMatchingWithFunctionsEnabled = retailerSettings?.enableMatching === MATCHING.RICARDO;
    const { MessageWithAlerts } = useEquivalentProductMatchingSummaryAlerts(
        temporaryMaterial && isEquivalentProductMatchingWithFunctionsEnabled
            ? {
                  material: temporaryMaterial,
              }
            : matchingCheckSkipToken
    );

    const requestedColor = useAppSelector(selectRequestedColor);

    const resetSelections = useCallback(() => {
        resetActiveShape();
        clearDrawingShapesUndoStack();
        dispatch(resetCookingAppliances());
        dispatch(resetCountertopAreaQuantity());
        dispatch(resetSelectedColor());
        dispatch(resetSelectedCornerRadius());
        dispatch(resetSelectedCustomSide());
        dispatch(resetSelectedCutouts());
        dispatch(resetSelectedDiagonalCorner());
        dispatch(resetSelectedEdge());
        dispatch(resetSinks());
        dispatch(resetDishwashers());
        dispatch(resetRefrigerators());
        resetDrawingShapes();
        dispatch(resetFaucets());
        resetPricedProducts();
        dispatch(resetSelectedChipMinimizers());
        resetSelectedCutouts();
        resetSelectedOptionalProducts();
        dispatch(resetSelectedTearOuts());
    }, [
        clearDrawingShapesUndoStack,
        dispatch,
        resetActiveShape,
        resetDrawingShapes,
        resetPricedProducts,
        resetSelectedOptionalProducts,
    ]);

    const { trackSelectMaterial } = useAnalyticsEvents();

    const changeToRequestedColor = useCallback(
        (color: ColorModel | undefined) => {
            if (color) {
                dispatch(updateRequestedColor({ color }));
            }
        },
        [dispatch]
    );

    const tryChangeMaterial = useCallback(
        async (material: MaterialModel, color?: ColorModel) => {
            if (material.description === selectedMaterial?.description) {
                changeToRequestedColor(color); // deal with external request to change both material and color
                return;
            }

            if (isEquivalentProductMatchingWithFunctionsEnabled) {
                await replaceIfNoAlerts({
                    targetParameters: {
                        material,
                        color,
                    },
                    onAlertsCallback: () => {
                        // onConfirmChangeMaterial handles the change
                        setTemporaryMaterial(material);
                        setIsModalVisible(true);
                    },
                    onNoAlertsCallback: () => {
                        setSelectedMaterialWithHistory(material);
                        trackSelectMaterial({ material, color });
                        changeToRequestedColor(color); // deal with external request to change both material and color
                    },
                });
            } else if (areThereShapesInTheDrawingArea) {
                setIsModalVisible(true);
                setTemporaryMaterial(material);
            } else {
                setSelectedMaterialWithHistory(material);
                trackSelectMaterial({ material, color });
                changeToRequestedColor(color); // deal with external request to change both material and color
                if (retailerSettings?.enableMatching || isEquivalentProductMatchingWithFunctionsEnabled) {
                    resetSelections();
                }
            }
        },
        [
            areThereShapesInTheDrawingArea,
            changeToRequestedColor,
            isEquivalentProductMatchingWithFunctionsEnabled,
            replaceIfNoAlerts,
            resetSelections,
            retailerSettings?.enableMatching,
            selectedMaterial?.description,
            setSelectedMaterialWithHistory,
        ]
    );

    const currentColorRequest = useRef<MaterialAndColor | undefined>();
    //externally requested material and color change
    useEffect(() => {
        /**
         * compares a new color change request to a previous one, returns true if _some specific_ properties differ
         * does not do a direct object comparison becuase a second request for the same values should not process twice
         * @param newRequest the new
         * @param currentRequest
         */
        const isSameRequest = (newRequest: MaterialAndColor, currentRequest: MaterialAndColor | undefined) => {
            return (
                newRequest.material?.description === currentRequest?.material?.description &&
                newRequest.color.productId === currentRequest?.color.productId
            );
        };
        if (!requestedColor?.material || isSameRequest(requestedColor, currentColorRequest.current)) {
            return;
        }
        currentColorRequest.current = requestedColor;
        tryChangeMaterial(requestedColor.material, requestedColor.color);
    }, [requestedColor, tryChangeMaterial]);

    /**
     * clear all selections if we don't have any stores - can't have a design without a store
     */
    useEffect(() => {
        if (!isFetchingStores && !stores.length) {
            resetSelections();
        }
    }, [
        isFetchingStores,
        stores.length,
        // resetSelections, DO NOT ADD THIS, even though we should, triggers an infinite re-render loop
    ]);

    const onConfirmChangeMaterial = async () => {
        if (!temporaryMaterial) {
            return;
        }
        if (isEquivalentProductMatchingWithFunctionsEnabled) {
            await replaceProductsWithEquivalents({
                material: temporaryMaterial,
                color: requestedColor?.color,
            });
        }

        setSelectedMaterialWithHistory(temporaryMaterial);
        if (requestedColor?.color) {
            changeToRequestedColor(requestedColor?.color); // deal with external request to change both material and color
        } else {
            // otherwise clear the selected color and use the default for this material
            dispatch(resetSelectedColor());
        }

        if (!retailerSettings?.enableMatching) {
            resetSelections();
        }
        setTemporaryMaterial(undefined);
        setIsModalVisible(false);
    };
    const onCancelChangingMaterialFromModal = () => {
        setIsModalVisible(false);
        setTemporaryMaterial(undefined);
        currentColorRequest.current = undefined;
        dispatch(resetRequestedColor());
    };

    const showDetails = !!zipcode && !!materials.length;
    const isFetchingData = isFetchingMaterials || isFetchingStores;

    return (
        <>
            <LoaderOnAlerts />
            {isEquivalentProductMatchingWithFunctionsEnabled ? (
                <ConfirmationDialogWithBanner
                    visible={isModalVisible}
                    banner={
                        <DialogBanner
                            icon={<VisualizeIcon />}
                            bannerText={t('EQUIVALENT_PRODUCT_MATCHING.ALERTS_TEXT')}
                        />
                    }
                    onCancel={onCancelChangingMaterialFromModal}
                    onConfirm={onConfirmChangeMaterial}
                    icon={<VisualizeIcon />}
                    title={t('SCROLLABLE_AREA.MATERIAL.MODAL_TITLE', {
                        newMaterial: temporaryMaterial?.description,
                    })}
                    content={<MessageWithAlerts />}
                />
            ) : null}

            {!isEquivalentProductMatchingWithFunctionsEnabled ? (
                <ConfirmationDialog
                    visible={isModalVisible}
                    onCancel={onCancelChangingMaterialFromModal}
                    onConfirm={onConfirmChangeMaterial}
                    icon={<VisualizeIcon />}
                    title={t('SCROLLABLE_AREA.MATERIAL.MODAL_TITLE', {
                        newMaterial: temporaryMaterial?.description,
                    })}
                    content={t('SCROLLABLE_AREA.MATERIAL.MATERIAL_CHANGE_WARNING')}
                />
            ) : null}

            {showDetails ? (
                <CardItem
                    className={classNames({ 'dim-section': disabled })}
                    id={step}
                    title={t('SCROLLABLE_AREA.MATERIAL.TITLE')}
                    stepInfo={stepInfo}
                    description={t('SCROLLABLE_AREA.MATERIAL.DESCRIPTION')}
                    disabled={disabled}
                >
                    {isFetchingData && <Spin style={{ fontSize: 24 }} />}
                    {materials.length ? (
                        <Row gutter={[10, 10]} style={{ overflowX: 'auto', flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
                            {materials.map((material: MaterialModel, id: number) => (
                                <Col key={id} sm={24} lg={12} xl={8} style={{ minWidth: '20rem', maxWidth: '27rem' }}>
                                    <MaterialCard
                                        material={material}
                                        selected={selectedMaterial?.id === material.id}
                                        onClick={() => tryChangeMaterial(material)}
                                    />
                                </Col>
                            ))}
                        </Row>
                    ) : null}
                </CardItem>
            ) : (
                <CardItem
                    className={classNames({ 'dim-section': disabled })}
                    id={step}
                    title={t('SCROLLABLE_AREA.MATERIAL.TITLE')}
                    stepInfo={stepInfo}
                    collapsed
                    disabled={disabled}
                />
            )}
        </>
    );
};

export default Materials;
